/**
 * documentEngineConfig.tsx — config silnika dla klas gołych (commercial /
 * internal / specjalne), keystone unifikacja-formatek Krok 1.
 *
 * „Owinięcie pilota w config" (decyzja Z2): jedna instancja
 * `KartotekaEngineConfig<BareDocFormData>` odtwarzająca dziś-stan
 * `useDocumentRenderField` BAJT-W-BAJT — te same nadpisania widgetów, etykiety
 * z i18n (`tenant.documents.shared.fields.*`, NIE z definicji — Z3 dla
 * dokumentów dokończymy przy kontrahencie/pracowniku), te same testid'y
 * (`${docClass}-doc-field-*`), koercja `'' → null` i fallback 403.
 *
 * Źródłem prawdy pól core pozostaje `DOCUMENT_FIELD_CONFIG` (stateKey / colSpan
 * / maxLength / rows / fieldType / override) — builder tłumaczy `override` →
 * renderer, resztę przepisuje. Renderery nadpisań są kopią 1:1 gałęzi `switch`
 * z pilota; prefiks testid czytają z kontekstu (`ctx.testIdPrefix`), więc jeden
 * zestaw obsługuje wszystkie klasy gołe (commercial / internal / specjalne).
 */

import { makeDocumentCategoryRenderer } from '@/components/documents/documentCategoryRenderer';
import { documentCompanyFieldConfig } from '@/components/documents/documentCompanyRenderer';
import {
  type BareDocFormData,
  SourceSelect,
  StatusSelect,
  TextInput,
  UrgencySelect,
} from '@/components/documents/documentCoreRenderers';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderer,
  KartotekaEngineConfig,
  LabelSourceArgs,
} from '@/components/kartoteki/engine/engine-config';
import { UserPicker } from '@/components/ui/UserPicker';
import { UserTablePickerButton } from '@/components/ui/UserTablePickerButton';

import { type DocFieldConfig, DOCUMENT_FIELD_CONFIG } from './documentFieldConfig';

// ─── Renderery nadpisań (kopia 1:1 gałęzi `switch` z pilota) ────────────────

const systemNumberRenderer: FieldRenderer<BareDocFormData> = ({
  form,
  isCreate,
  testIdPrefix,
  t,
}) => (
  // Read-only zawsze (auto-generowany przez backend) — niezależnie od `editable`.
  <Field label={t('tenant.documents.shared.fields.systemNumber')}>
    <TextInput
      testId={`${testIdPrefix}-systemNumber`}
      value={form.systemNumber ?? ''}
      onChange={() => {}}
      disabled
      placeholder={isCreate ? t('tenant.documents.shared.systemNumberPlaceholder') : ''}
    />
  </Field>
);

const statusRenderer: FieldRenderer<BareDocFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field label={t('tenant.documents.shared.fields.status')} required error={getError?.('status')}>
    <StatusSelect
      testId={`${testIdPrefix}-status`}
      value={form.status}
      onChange={(v) => setField({ status: v })}
      disabled={!editable}
      invalid={hasError?.('status')}
    />
  </Field>
);

const urgencyRenderer: FieldRenderer<BareDocFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field label={t('tenant.documents.shared.fields.urgency')} required error={getError?.('urgency')}>
    <UrgencySelect
      testId={`${testIdPrefix}-urgency`}
      value={form.urgency}
      onChange={(v) => setField({ urgency: v })}
      disabled={!editable}
      invalid={hasError?.('urgency')}
    />
  </Field>
);

const sourceRenderer: FieldRenderer<BareDocFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  hasError,
  getError,
  t,
}) => (
  <Field label={t('tenant.documents.shared.fields.source')} required error={getError?.('source')}>
    <SourceSelect
      testId={`${testIdPrefix}-source`}
      value={form.source}
      onChange={(v) => setField({ source: v })}
      disabled={!editable}
      invalid={hasError?.('source')}
    />
  </Field>
);

const ownerUserIdRenderer: FieldRenderer<BareDocFormData> = ({
  form,
  setField,
  editable,
  testIdPrefix,
  companyId,
  t,
}) => (
  <Field label={t('tenant.documents.shared.fields.ownerUserId')}>
    <div className="flex items-start gap-2">
      <div className="min-w-0 flex-1">
        <UserPicker
          value={form.ownerUserId}
          onChange={(v) => setField({ ownerUserId: v })}
          companyId={companyId}
          disabled={!editable}
          data-testid={`${testIdPrefix}-ownerUserId`}
        />
      </div>
      <UserTablePickerButton
        value={form.ownerUserId}
        onPick={(id) => setField({ ownerUserId: id })}
        companyId={companyId}
        disabled={!editable}
        data-testid={`${testIdPrefix}-ownerUserId-open-picker`}
      />
    </div>
  </Field>
);

// Kategoria (oś `record_categories`, kartoteka_key='documents') — renderer WSPÓLNY dla
// wszystkich 7 klas dokumentów, patrz documentCategoryRenderer.tsx.
const categoryRenderer = makeDocumentCategoryRenderer<BareDocFormData>();

// ─── Builder: DocFieldConfig (pilot) → CoreFieldConfig<BareDocFormData> ──────

function translate(cfg: DocFieldConfig): CoreFieldConfig<BareDocFormData> {
  const base: CoreFieldConfig<BareDocFormData> = {
    stateKey: cfg.stateKey,
    fieldType: cfg.fieldType,
    ...(cfg.colSpan !== undefined ? { colSpan: cfg.colSpan } : {}),
    ...(cfg.maxLength !== undefined ? { maxLength: cfg.maxLength } : {}),
    ...(cfg.rows !== undefined ? { rows: cfg.rows } : {}),
  };
  switch (cfg.override) {
    case 'systemNumber':
      return { ...base, renderer: systemNumberRenderer };
    case 'status':
      return { ...base, renderer: statusRenderer };
    case 'urgency':
      return { ...base, renderer: urgencyRenderer };
    case 'source':
      return { ...base, renderer: sourceRenderer };
    case 'ownerUserId':
      return { ...base, renderer: ownerUserIdRenderer };
    case 'category':
      return { ...base, renderer: categoryRenderer };
    case 'documentTypeId':
      // Strukturalne: badge typu renderuje nagłówek strony, poza drzewem layoutu.
      return { ...base, structuralNull: true };
    case undefined:
      // Natywne — wspólny edytor per fieldType (delegacja do ExtFieldValueInput).
      return base;
  }
}

/** Mapa slug → CoreFieldConfig, wspólna dla commercial, internal i specjalne (identyczne pola core). */
const DOCUMENT_FIELD_CONFIGS: Record<string, CoreFieldConfig<BareDocFormData>> = {
  ...Object.fromEntries(
    Object.entries(DOCUMENT_FIELD_CONFIG).map(([slug, cfg]) => [slug, translate(cfg)]),
  ),
  // Firma dokumentu — tylko do odczytu, opt-in z układu (NIE w coreFieldSlugs): patrz documentCompanyRenderer.tsx.
  company_id: documentCompanyFieldConfig<BareDocFormData>(),
};

/**
 * Slugi core w kolejności seeda (bez document_type_id — strukturalne, badge w
 * nagłówku). Kopia list `COMMERCIAL_CORE_FIELD_SLUGS` / inline w stronach pilota
 * — konsumuje je top-up strony (synteza zakładki 'data'). Krok 1 nie zmienia
 * stron; pole udostępnione dla przyszłego podpięcia.
 */
const DOCUMENT_CORE_FIELD_SLUGS = [
  'system_number',
  'title',
  'status',
  'document_date',
  'description',
  'urgency',
  'owner_user_id',
  'category_id',
  'notes',
  'rejection_reason',
  'source',
  'external_id',
  'external_source',
];

/** Etykiety pól natywnych z i18n (parytet z pilotem) — NIE z definicji (Z3 dla dokumentów później). */
const i18nLabelSource = ({ cfg, t }: LabelSourceArgs<BareDocFormData>): string =>
  t(`tenant.documents.shared.fields.${String(cfg.stateKey)}`);

/** Config silnika dla klasy gołej (commercial/internal/specjalne). moduleSlug = docClass. */
export function buildDocumentEngineConfig(
  docClass: 'commercial' | 'internal' | 'specjalne',
): KartotekaEngineConfig<BareDocFormData> {
  return {
    moduleSlug: docClass,
    testIdPrefix: `${docClass}-doc-field`,
    coreFieldSlugs: DOCUMENT_CORE_FIELD_SLUGS,
    fields: DOCUMENT_FIELD_CONFIGS,
    labelSource: i18nLabelSource,
    coerceEmptyToNull: true,
  };
}
